import React, { memo, useCallback } from "react";
import { Chip, Tooltip } from "@mui/material";
import SingleDateField from "../../Calendar/SingleDateField";
import classes from "./DateRangeWithPresets.module.scss";

// 検索期間(From/To)の入力と、ワンクリックで期間を差し替えられる
// プリセットチップをまとめたコンポーネント。
//
// 期間は常に From/To 両方が有効な Date であることを前提にしている。
// null や不正な日付が入力された場合は onChange を呼ばず、直前の
// 有効な期間を維持する(検索条件としての期間は常に必須のため)。
//
// From/To の前後関係が崩れる入力(Fromが Toより後、Toが Fromより前)が
// 来た場合は、複雑な自動補正はせず「選択した側の日付に両方をそろえる」
// だけのシンプルな挙動にしている。

// 有効な Date かどうかを判定する(null や不正な日付を弾くためのガード)。
const isValidDate = (date) => date instanceof Date && !isNaN(date.getTime());

// 年月日だけを数値化する(時刻部を無視して比較するための下請けヘルパー)。
const toDateOnlyValue = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

// 年月日が一致するか(時刻は無視)。プリセットの選択中判定に使う。
const isSameDateOnly = (a, b) =>
  isValidDate(a) && isValidDate(b) && toDateOnlyValue(a) === toDateOnlyValue(b);

// a の年月日が b より後か(時刻は無視)。
const isAfterDateOnly = (a, b) => toDateOnlyValue(a) > toDateOnlyValue(b);

// a の年月日が b より前か(時刻は無視)。
const isBeforeDateOnly = (a, b) => toDateOnlyValue(a) < toDateOnlyValue(b);

const DateRangeWithPresets = memo((props) => {
  const { from, to, onChange, presets = [], disabled = false } = props;

  const handleFromChange = useCallback(
    (date) => {
      if (!isValidDate(date)) return; // 無効な入力は無視(期間は常に必須)
      if (isAfterDateOnly(date, to)) {
        // Fromの方がToより後 → 複雑な補正はせずToを同日にそろえる
        onChange({ from: date, to: date });
      } else {
        onChange({ from: date, to });
      }
    },
    [to, onChange]
  );

  const handleToChange = useCallback(
    (date) => {
      if (!isValidDate(date)) return; // 無効な入力は無視(期間は常に必須)
      if (isBeforeDateOnly(date, from)) {
        // ToがFromより前 → 複雑な補正はせずFromを同日にそろえる
        onChange({ from: date, to: date });
      } else {
        onChange({ from, to: date });
      }
    },
    [from, onChange]
  );

  const handlePresetClick = useCallback(
    (preset) => {
      onChange({ from: preset.from, to: preset.to });
    },
    [onChange]
  );

  return (
    <div className={classes.root}>
      <div className={classes.fields}>
        <SingleDateField
          label="期間From"
          value={from}
          onChange={handleFromChange}
          disabled={disabled}
          width={140}
        />
        <SingleDateField
          label="期間To"
          value={to}
          onChange={handleToChange}
          disabled={disabled}
          width={140}
        />
      </div>
      <div className={classes.presets}>
        {presets.map((preset) => {
          const isActive =
            isSameDateOnly(preset.from, from) && isSameDateOnly(preset.to, to);
          const chip = (
            <Chip
              label={preset.label}
              size="small"
              color={isActive ? "primary" : "default"}
              variant={isActive ? "filled" : "outlined"}
              disabled={disabled}
              clickable
              onClick={() => handlePresetClick(preset)}
            />
          );
          return preset.title ? (
            // disabled中はChip自体がpointer-events:noneになりTooltipのホバーが
            // 届かなくなるため、spanでラップして常に発火できるようにする
            // (MUI公式が案内する「disabled要素をTooltipで包む」パターン)。
            <Tooltip key={preset.id} title={preset.title}>
              <span>{chip}</span>
            </Tooltip>
          ) : (
            <React.Fragment key={preset.id}>{chip}</React.Fragment>
          );
        })}
      </div>
    </div>
  );
});

export default DateRangeWithPresets;
